// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraSource } from "@capacitor/camera";
import { Stack } from "@mui/material";
import { type CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { BottomSheetIllustrationContent } from "@src/appV2/redesign/system/ui-components";

export interface ChoosePhotoContentProps {
  showDescriptionText: boolean;
  isTimesheetUploaded: boolean;
  onPhotoTaken: (photo: UploadFileData) => void;
  onError: (error: Error | CameraPermissionDeniedError) => void;
  onCloseClick: () => void;
}

export function ChoosePhotoContent(props: ChoosePhotoContentProps) {
  const { showDescriptionText, isTimesheetUploaded, onPhotoTaken, onError, onCloseClick } = props;

  const takePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess: (photo) => {
      onPhotoTaken(photo);
    },
    onError,
  });

  const getIllustrationProps = () => {
    if (isTimesheetUploaded) {
      return {
        illustrationType: "document-review" as const,
        title: "Upload new timesheet?",
        description:
          "A timesheet has already been uploaded and not yet approved. Please re-upload if you wish to make changes.",
      };
    }

    return {
      illustrationType: "document-upload" as const,
      title: "Upload timesheet",
      description: showDescriptionText ? "This workplace still requires paper timesheets." : "",
    };
  };

  const illustrationProps = getIllustrationProps();
  const chooseFromLibrary = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess: (photo) => {
      onPhotoTaken(photo);
    },
    onError,
  });

  return (
    <>
      <BottomSheetIllustrationContent
        illustrationType={illustrationProps.illustrationType}
        title={illustrationProps.title}
        description={illustrationProps.description}
      />
      <Stack direction="column" spacing={4} padding={4} paddingBottom={6} justifyContent="center">
        <Button size="large" variant="contained" onClick={takePhoto}>
          Take a photo
        </Button>
        <Button size="large" variant="outlined" onClick={chooseFromLibrary}>
          Upload from library
        </Button>

        <IconButton
          aria-label="Cancel timesheet upload"
          size="large"
          variant="outlined"
          iconType="close"
          sx={{ alignSelf: "center" }}
          onClick={onCloseClick}
        />
      </Stack>
    </>
  );
}
